import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  AfterViewInit,
  Output,
  inject,
} from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { FiltersOptionsType, IFiltersOptionsParam } from 'src/app/data/store/models/filters.model';
import {
  Buttons,
  ButtonTextSizes,
  FiltersModalSteps,
  FiltersOptions,
  FiltersResetTypes,
  TextSizes,
  TextType,
} from 'src/app/shared/enums';
import { ICountryOrCity, IRegion } from 'src/app/shared/services/departments/departments.type';
import { IShop, IUserPosition } from 'src/app/shared/services/user/user.type';
import { Store } from '@ngrx/store';
import {
  activeOptionFilterSearchValueChange,
  applySingleFilterAction,
  clearFiltersElementsList,
  clearFiltersInit,
  getFilterElementsList,
  updateFiltersAction,
} from 'src/app/data/store/actions/filters.actions';
import { IDepartment } from 'src/app/shared/services/posts/posts.type';
import {
  selectActiveFiltersElementsList,
  selectElementsListIsLoading,
  selectFiltersState,
  selectIsAllElementsFetched,
} from '../../../../../data/store/selectors/filters.selectors';
import { FiltersOptionsNames } from 'src/app/shared/constants';
import { Observable } from 'rxjs';
import { UtilsService } from 'src/app/shared/services/utils.service';
import { IonicModule } from '@ionic/angular';
import { AsyncPipe } from '@angular/common';
import { ImageComponent } from '../../../image/image.component';
import { TextComponent } from '../../../text/text.component';
import { ButtonComponent } from '../../../button/button.component';
import { IonScrollBarDirective } from '../../../../directives/ion-scroll-bar/ion-scroll-bar.directive';
import { PaginationContainerComponent } from '../../../pagination-container/pagination-container.component';
import { CheckIsSelectedPipe } from '../../../../pipe/check-filters-selection-status/check-is-selected.pipe';
import { GetFullNamePipe } from '../../../../pipe/get-full-name/get-full-name.pipe';

@UntilDestroy()
@Component({
  selector: 'app-filters-list',
  templateUrl: './filters-list.component.html',
  styleUrls: ['./filters-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    IonicModule,
    ImageComponent,
    TextComponent,
    ButtonComponent,
    IonScrollBarDirective,
    PaginationContainerComponent,
    AsyncPipe,
    CheckIsSelectedPipe,
    GetFullNamePipe,
  ],
})
export class FiltersListComponent implements OnInit, AfterViewInit, OnDestroy {
  private _store = inject(Store);
  private _cdr = inject(ChangeDetectorRef);
  private _utils = inject(UtilsService);

  private _isFilterListInitialized = false;

  @Input() activeFilterList: FiltersOptionsType | null = null;

  @Input() initialFilters: IFiltersOptionsParam | null = null;

  @Input() isCheckboxType = false;

  @Input() isOnlyFiltersList = false;

  @Output() public emitStepChange = new EventEmitter<string>();

  readonly TextSizes = TextSizes;

  readonly TextType = TextType;

  readonly Buttons = Buttons;

  readonly ButtonTextSizes = ButtonTextSizes;

  readonly filtersOptions = FiltersOptions;

  readonly filtersOptionsNames = FiltersOptionsNames;

  public elements: any[] = [];

  public onlyFavorite = false;

  public isNeedSearch = false;

  public selectedElements: (IShop | ICountryOrCity | IRegion | IDepartment | IUserPosition)[] = [];

  public isLoading$: Observable<boolean> = this._store.select(selectElementsListIsLoading);

  public isAllListDataFetched$: Observable<boolean | null> = this._store.select(
    selectIsAllElementsFetched,
  );

  public activeFilterElementsList$: Observable<any[]> = this._store.select(
    selectActiveFiltersElementsList,
  );

  public filtersState$ = this._store.select(selectFiltersState);

  public ngOnInit(): void {
    if (this.initialFilters && this.activeFilterList) {
      this.selectedElements = this.initialFilters[this.activeFilterList]?.params || [];
    }

    this.activeFilterElementsList$.pipe(untilDestroyed(this)).subscribe((elements) => {
      this.elements = elements;

      this._cdr.detectChanges();
    });

    this.filtersState$.pipe(untilDestroyed(this)).subscribe((filters) => {
      this.initialFilters = Object.assign({}, filters.filtersList) || null;
    });

    this.isNeedSearch =
      this.activeFilterList !== FiltersOptions.regions &&
      this.activeFilterList !== FiltersOptions.depts;
  }

  public ngAfterViewInit(): void {
    setTimeout(() => {
      this._isFilterListInitialized = true;
    });
  }

  public getElements() {
    if (!this.activeFilterList) {
      return;
    }

    this._store.dispatch(
      getFilterElementsList({
        activeFilterOption: this.activeFilterList,
      }),
    );
  }

  public clearFiltersList() {
    let isNeedClearStore = false;

    if (this.initialFilters && this.activeFilterList) {
      isNeedClearStore = Boolean(this.initialFilters[this.activeFilterList]?.params?.length);
    }

    if (isNeedClearStore && this.activeFilterList) {
      this._store.dispatch(
        clearFiltersInit({
          clearType: FiltersResetTypes.resetCurrent,
          activeFilterOption: this.activeFilterList,
        }),
      );
    }
    this.clearChosenElem();
  }

  public applyFiltersList() {
    if (this.initialFilters && this.activeFilterList) {
      this.initialFilters = Object.assign(
        { ...this.initialFilters },
        {
          [this.activeFilterList]: {
            ...this.initialFilters[this.activeFilterList],
            params: this.selectedElements,
          },
        },
      );
    }

    if (
      this.initialFilters &&
      this.activeFilterList &&
      this.initialFilters[this.activeFilterList]?.isFilterSingleButton
    ) {
      this._store.dispatch(
        applySingleFilterAction({
          data: this.initialFilters,
          activeFilterOption: this.activeFilterList,
        }),
      );
    } else {
      this._store.dispatch(
        updateFiltersAction({
          filtersOptionsParams: this.initialFilters,
          activeFilterOption: this.activeFilterList,
        }),
      );
    }

    if (!this.isOnlyFiltersList) {
      this.backToMenu();
    } else {
      this._utils.dismissModal();
    }
  }

  public backToMenu() {
    this.emitStepChange.emit(FiltersModalSteps.menu);
  }

  public searchValueChange(event: any) {
    if (!this.activeFilterList) {
      return;
    }

    this._store.dispatch(
      activeOptionFilterSearchValueChange({
        searchValue: event.detail.value ? event.detail.value : null,
        activeFilterOption: this.activeFilterList,
      }),
    );
  }

  public onChange(event: any) {
    if (!this._isFilterListInitialized) {
      return;
    }

    let selectedElement: IShop | ICountryOrCity | IRegion | IDepartment | IUserPosition | null =
      null;

    if (this.isCheckboxType && !event.detail.checked) {
      if (!this.selectedElements.some((el) => el.id === event.detail.value)) {
        return;
      }

      this.selectedElements = this.selectedElements.filter((el) => el.id !== event.detail.value);
      return;
    }

    selectedElement = this.elements.find((el) => el.id === event.detail.value);

    if (selectedElement && (!this.isCheckboxType || !this.selectedElements.length)) {
      this.selectedElements = [selectedElement];
    } else if (this.selectedElements.find((el) => el.id === event.detail.value)) {
      this.selectedElements = this.selectedElements.filter((el) => el.id !== event.detail.value);
    } else if (selectedElement) {
      this.selectedElements = [...this.selectedElements, selectedElement];
    }
  }

  public clearChosenElem() {
    this.selectedElements = [];
  }

  public trackByFn(
    index: number,
    obj: IShop | ICountryOrCity | IRegion | IDepartment | IUserPosition,
  ) {
    return obj.id;
  }

  ngOnDestroy(): void {
    this._store.dispatch(clearFiltersElementsList());
  }
}
